
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { ProfessionalDirectoryHeader } from './ProfessionalDirectoryHeader';
import { ProfessionalSearchBar } from './ProfessionalSearchBar';
import { ProfessionalFilterSidebar } from './ProfessionalFilterSidebar';
import { ProfessionalResultsPane } from './ProfessionalResultsPane';
import { ProfessionalFilterDrawer } from './ProfessionalFilterDrawer';
import { useProfessionalDirectoryQuery } from './useProfessionalDirectoryQuery';

interface ProfessionalDirectoryLayoutProps {
  title?: string;
  description?: string;
}

export function ProfessionalDirectoryLayout({ 
  title = "Professional Directory",
  description = "Find trusted professionals in your area."
}: ProfessionalDirectoryLayoutProps) {
  const {
    search,
    setSearch,
    location,
    setLocation,
    radius,
    setRadius,
    licenseVerifiedOnly,
    setLicenseVerifiedOnly,
    sortBy,
    setSortBy,
    filters,
    setFilters,
    results,
    loading,
    totalCount,
    clearAllFilters,
  } = useProfessionalDirectoryQuery();

  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const handleFiltersApply = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  const handleFilterRemove = (key: string, value: string) => {
    const newFilters = { ...filters };
    
    if (key === 'experienceYearsMin') {
      newFilters.experienceYearsMin = undefined;
    } else if (key === 'experienceYearsMax') {
      newFilters.experienceYearsMax = undefined;
    } else if (Array.isArray(newFilters[key as keyof typeof newFilters])) {
      const currentArray = (newFilters[key as keyof typeof newFilters] as string[]) || [];
      (newFilters as any)[key] = currentArray.filter((item: string) => item !== value);
    } else {
      (newFilters as any)[key] = 'all';
    }
    
    setFilters(newFilters);
  };

  const handleFiltersReset = () => {
    clearAllFilters();
    setMobileFiltersOpen(false);
  };

  return (
    <>
      <Helmet>
        <title>{title} | SeniorThrive</title>
        <meta name="description" content={description} />
        <meta name="keywords" content="professional care, occupational therapist, physical therapist, CAPS, elder law attorney, home modification, financial planning" />
        <link rel="canonical" href="https://seniorthrive.com/professionals/directory" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50/50">
        <div className="container mx-auto px-4 py-8">
          {/* Back to Caregivers Link */}
          <div className="mb-6">
            <Button variant="ghost" asChild className="gap-2 text-muted-foreground hover:text-foreground">
              <Link to="/caregivers/directory">
                <ArrowLeft className="w-4 h-4" />
                Back to Caregivers Directory
              </Link>
            </Button>
          </div>

          <ProfessionalDirectoryHeader />
          
          <ProfessionalSearchBar
            search={search}
            onSearchChange={setSearch}
            location={location}
            onLocationChange={setLocation}
            radius={radius}
            onRadiusChange={setRadius}
            licenseVerifiedOnly={licenseVerifiedOnly}
            onLicenseVerifiedOnlyChange={setLicenseVerifiedOnly}
          />

          {/* Two-column layout: Filters + Results */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Desktop Filters Sidebar */}
            <div className="hidden lg:block lg:col-span-3">
              <div className="sticky top-8">
                <ProfessionalFilterSidebar
                  filters={filters}
                  onFiltersChange={handleFiltersApply}
                  onFiltersReset={handleFiltersReset}
                />
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-9">
              <ProfessionalResultsPane
                results={results}
                loading={loading}
                totalCount={totalCount}
                filters={filters}
                sortBy={sortBy}
                onSortChange={setSortBy}
                onFilterRemove={handleFilterRemove}
                onClearAllFilters={clearAllFilters}
                onShowMobileFilters={() => setMobileFiltersOpen(true)}
              />
            </div>
          </div>

          {/* Cross-navigation to Caregivers */}
          <div className="mt-12 p-6 bg-white rounded-2xl border shadow-sm">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Need Day-to-Day Care?
              </h3>
              <p className="text-muted-foreground mb-4">
                Find trusted caregivers for personal care, companionship, and daily support.
              </p>
              <Button asChild className="bg-st_light_blue hover:bg-st_light_blue/90">
                <Link to="/caregivers/directory">
                  Browse Caregivers Directory
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Filter Drawer */}
        <ProfessionalFilterDrawer
          open={mobileFiltersOpen}
          onOpenChange={setMobileFiltersOpen}
          filters={filters}
          onFiltersApply={handleFiltersApply}
          onFiltersReset={handleFiltersReset}
        />
      </div>
    </>
  );
}
